import { useState, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';

// ─── Types ────────────────────────────────────────────────────────────────────

export type LoginAuthState =
  | 'idle'
  | 'authenticating'
  | 'needs_2fa'
  | 'verifying_2fa'
  | 'authenticated'
  | 'unauthenticated'
  | 'error';

export interface LoginFSMContextState {
  state: LoginAuthState;
  user: User | null;
  accessToken: string | null;
  error: string | null;
}

export type PortalUserType = 'admin' | 'consultant' | 'workday_services_client';

interface UseLoginFSMOptions {
  userType: PortalUserType;
  onSignOut?: () => Promise<void>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// iOS Safari delays session writes after signInWithPassword. Poll until readable.
async function waitForSession(maxWaitMs = 2000): Promise<void> {
  const interval = 150;
  const maxAttempts = Math.ceil(maxWaitMs / interval);
  for (let i = 0; i < maxAttempts; i++) {
    const { data } = await supabase.auth.getSession();
    if (data.session) return;
    await new Promise(r => setTimeout(r, interval));
  }
}

// ─── FSM hook ─────────────────────────────────────────────────────────────────

export function useLoginFSM({ userType, onSignOut }: UseLoginFSMOptions) {
  const [auth, setAuth] = useState<LoginFSMContextState>({
    state: 'idle',
    user: null,
    accessToken: null,
    error: null,
  });

  const transition = useCallback((next: Partial<LoginFSMContextState>) => {
    setAuth(prev => ({ ...prev, ...next }));
  }, []);

  const doSignOut = useCallback(async () => {
    if (onSignOut) await onSignOut();
    else await supabase.auth.signOut();
    transition({ state: 'unauthenticated', user: null, accessToken: null, error: null });
  }, [onSignOut, transition]);

  // ── Step 1: called by login handler immediately after signInWithPassword succeeds ──
  // This is the ONLY place that drives authenticating → needs_2fa | authenticated.
  const checkAndTransition = useCallback(async (user: User, token: string) => {
    transition({ state: 'authenticating', user, error: null });

    try {
      if (userType === 'admin') {
        const { data, error } = await supabase
          .from('user_roles')
          .select('is_2fa_enabled')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error || data === null) {
          console.error('[FSM] admin user_roles lookup failed — blocking', error?.message);
          await doSignOut();
          transition({ state: 'error', error: 'Account configuration error. Contact support.' });
          return;
        }

        if (data.is_2fa_enabled) {
          transition({ state: 'needs_2fa', user, accessToken: token, error: null });
          return;
        }
      }

      if (userType === 'consultant') {
        const { data, error } = await supabase
          .from('consultant_profiles')
          .select('is_2fa_enabled')
          .eq('id', user.id)
          .maybeSingle();

        if (error || data === null) {
          console.error('[FSM] consultant_profiles lookup failed — blocking', error?.message);
          await doSignOut();
          transition({ state: 'error', error: 'Account configuration error. Contact support.' });
          return;
        }

        if (data.is_2fa_enabled) {
          transition({ state: 'needs_2fa', user, accessToken: token, error: null });
          return;
        }
      }

      if (userType === 'workday_services_client') {
        const { data, error } = await supabase
          .from('workday_client_profiles')
          .select('is_2fa_enabled, invited_by')
          .eq('id', user.id)
          .maybeSingle();

        if (error || data === null) {
          console.error('[FSM] workday_client_profiles lookup failed — blocking', error?.message);
          await doSignOut();
          transition({ state: 'error', error: 'Account configuration error. Contact support.' });
          return;
        }

        if (!data.is_2fa_enabled && data.invited_by) {
          // Invited user who hasn't set up 2FA yet — reuses the needs_2fa path but
          // login page will render InvitedUser2FASetupModal instead.
          transition({ state: 'needs_2fa', user, accessToken: token, error: 'invited_setup' });
          return;
        }

        if (data.is_2fa_enabled) {
          transition({ state: 'needs_2fa', user, accessToken: token, error: null });
          return;
        }
      }

      // No 2FA — go straight to authenticated
      await waitForSession();
      transition({ state: 'authenticated', user, accessToken: token, error: null });
    } catch (err) {
      console.error('[FSM] Unexpected error during 2FA check — blocking access', err);
      await doSignOut();
      transition({ state: 'error', error: 'Unexpected error during sign-in. Please try again.' });
    }
  }, [userType, doSignOut, transition]);

  // ── Step 2: called by TwoFactorModal / InvitedUser2FASetupModal on success ──
  // needs_2fa → verifying_2fa → authenticated
  const on2FASuccess = useCallback((user: User) => {
    transition({ state: 'verifying_2fa' });
    transition({ state: 'authenticated', user, accessToken: null, error: null });
  }, [transition]);

  // ── Cancel: user dismissed the 2FA modal ──
  const on2FACancel = useCallback(async () => {
    await doSignOut();
  }, [doSignOut]);

  // ── Login entry point: called by form submit handler ──
  // Does NOT navigate. Navigation is the login page's sole responsibility,
  // triggered by watching auth.state === 'authenticated'.
  const login = useCallback(async (email: string, password: string) => {
    transition({ state: 'authenticating', error: null });

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      transition({ state: 'error', error: error.message });
      return { success: false, user: null, session: null };
    }

    return { success: true, user: data.user, session: data.session };
  }, [transition]);

  // ── Explicit sign-out ──
  const logout = useCallback(async () => {
    await doSignOut();
  }, [doSignOut]);

  // Convenience derived flags
  const needs2FA = auth.state === 'needs_2fa' && auth.error !== 'invited_setup';
  const needsInvited2FASetup = auth.state === 'needs_2fa' && auth.error === 'invited_setup';

  return {
    auth,
    needs2FA,
    needsInvited2FASetup,
    login,
    checkAndTransition,
    on2FASuccess,
    on2FACancel,
    logout,
  };
}
